import { apiFetch } from "./api";
import type { NotificationItem, NotificationType } from "./notifications-api";

export type { NotificationItem, NotificationType };

/**
 * The employee lane (FEAT-2026-013).
 *
 * Everything here talks to `/mobile/*`. That prefix reads oddly from a
 * browser, but in this codebase it does not mean "phone" — it is what selects
 * the **Employee** principal in the backend's `ClerkAuthGuard`, as opposed to
 * the **User** (admin) principal every other module in `src/lib` uses. A
 * browser carrying an employee's Clerk session authenticates against these
 * routes exactly as the mobile app does.
 *
 * Consequence worth remembering: an employee has no `User` row, so `/auth/me`
 * can never answer for them. `employeeApi.me()` is their equivalent.
 */

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
}

export type AttendanceSource = "MOBILE" | "WEB";
export type GpsStatus = "VALID" | "INVALID" | "UNKNOWN";
export type GeofenceStatus = "INSIDE" | "OUTSIDE" | "NOT_APPLICABLE";
export type WorkMode = "WFO" | "WFH" | "WFA";
/**
 * The day's status column, exactly as the server's enum has it. It is not the
 * day's lifecycle: an admin flag writes PENDING_REVIEW over it while the
 * sessions stay as they were, so read "checked in / out" from the sessions
 * (BR-159, `lib/today-status.ts`).
 */
export type AttendanceStatus =
  | "PRESENT"
  | "CHECKED_IN"
  | "CHECKED_OUT"
  | "PARTIAL"
  | "EXCEPTION"
  | "MISSED"
  | "OUTSIDE_GEOFENCE"
  | "PENDING_REVIEW";

export interface EmployeeSelf {
  id: string;
  empCode: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  status: string;
  /** Contract, permanent, and so on. Null when the tenant does not track it. */
  employmentType: string | null;
  departmentName: string | null;
  primarySite: { id: string; name: string } | null;
  shift: { id: string; name: string } | null;
  manager: { id: string; name: string } | null;
  /**
   * Last sign-in from the **mobile app** specifically. Null for someone who
   * only ever uses the browser — label it for what it measures, or a web-only
   * employee reads an empty "last login" as a bug.
   */
  lastMobileLoginAt: string | null;
  tenantId: string;
  tenantName: string;
  /** Active employees who report to them; above zero, the lane shows My team (BR-206). */
  directReportCount: number;
}

export interface EmployeeSession {
  id: string;
  attendanceRecordId: string;
  siteId: string | null;
  startAt: string;
  startLatitude: number | null;
  startLongitude: number | null;
  startGpsStatus: GpsStatus;
  startGeofenceStatus: GeofenceStatus;
  endAt: string | null;
  endLatitude: number | null;
  endLongitude: number | null;
  endGpsStatus: GpsStatus | null;
  endGeofenceStatus: GeofenceStatus | null;
  durationMinutes: number | null;
  closedBy: string | null;
  notes: string | null;
  workMode: WorkMode;
  /** Null on sessions recorded before provenance was tracked. */
  startSource: AttendanceSource | null;
  endSource: AttendanceSource | null;
  wfaRequestId: string | null;
  startAccuracyM: number | null;
  endAccuracyM: number | null;
}

export interface EmployeeDay {
  date: string;
  recordId: string | null;
  status: AttendanceStatus | null;
  checkInAt: string | null;
  checkOutAt: string | null;
  totalDurationMinutes: number;
  isException: boolean;
  workMode: WorkMode | null;
  /**
   * The day's target and how far it fell short (BR-155). Null where no
   * calendar was resolved, or where the organisation sets no minimum — never
   * treat null as "met", and never as zero.
   */
  expectedMinutes: number | null;
  shortByMinutes: number | null;
  leftEarlyByMinutes: number | null;
  sessions: EmployeeSession[];
}

/** Subset of `/mobile/config` the web lane actually uses. */
export interface EmployeeConfig {
  tenant: {
    organizationName: string | null;
    defaultLanguage: string;
    timezone: string;
    dateFormat: string;
  };
  attendance: {
    gracePeriodMins: number;
    lateThresholdMins: number;
    minWorkHoursPerDay: number;
    defaultWorkStartTime: string;
    defaultWorkEndTime: string;
    requireApprovalForCorrections: boolean;
    mode: "SINGLE_SESSION_PER_DAY" | "MULTIPLE_SESSIONS_PER_DAY";
    webCheckInEnabled: boolean;
    maxCheckInAccuracyM: number;
    wfaEnabled: boolean;
  };
  geofence: {
    enforcementEnabled: boolean;
    allowGpsOnlyFallback: boolean;
    defaultRadiusM: number;
  };
  notifications: { mobilePushEnabled: boolean };
  /**
   * Request settings (FEAT-2026-027). Optional so an older API without them
   * reads as "nothing required, line managers don't decide".
   */
  requests?: {
    lineManagerApprovalEnabled: boolean;
    /** Types that need an attachment (BR-210); a type not listed needs none. */
    attachmentRules: Array<{ type: RequestType; instruction: string | null }>;
    /**
     * The request types staff are offered, in order (FEAT-2026-031). Absent
     * from an older API; the form then uses its built-in list.
     */
    types?: WebRequestTypeConfig[];
  };
  updatedAt: string;
}

/** One offered request type in `/mobile/config` (FEAT-2026-031). */
export interface WebRequestTypeConfig {
  key: string;
  /** The tenant's name, shown as written; null = the built-in name. */
  label: string | null;
  description: string | null;
  dateMode: "single" | "range";
  attachment: { required: boolean; instruction: string | null };
}

/** A fix, as the browser reported it. `accuracy` is metres. */
export interface Position {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export interface CheckInBody extends Position {
  timestamp: string;
  siteId?: string;
  deviceInfo?: string;
}

export interface CheckOutBody extends Position {
  timestamp: string;
  remarks?: string;
}

/**
 * A site the employee may clock in at, from `GET /mobile/sites` — today just
 * their primary site. The radius is the **effective** one (the site's own, else
 * the organisation's default), so a map drawn from it matches the verdict.
 */
export interface EmployeeSite {
  id: string;
  code: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  geofenceRadiusM: number;
  geofenceMode: "GPS_AND_GEOFENCE" | "GPS_ONLY" | "MANUAL";
  status: string;
}

/** One day of the employee's own schedule (FEAT-2026-017). */
export interface ScheduleDay {
  date: string;
  /** ISO-8601 weekday, 1 = Monday … 7 = Sunday. */
  weekday: number;
  /**
   * Whether attendance is *expected*. Never a permission: someone may check in
   * on a day this reports false, and it records normally (BR-202). Phrase the
   * UI as "not scheduled", never "you cannot check in".
   */
  working: boolean;
  reason: "non-working-day" | "holiday" | "rostered-off" | null;
  holidayName: string | null;
  /** Null on a non-working day — deliberately, so it doesn't read as a shift. */
  startTime: string | null;
  endTime: string | null;
  shiftName: string | null;
  rostered: boolean;
}

export interface EmployeeSchedule {
  employeeId: string;
  timezone: string;
  defaultShiftName: string | null;
  days: ScheduleDay[];
}

// --- My team (FEAT-2026-026) — times and status, never place (BR-207) -------

export type TeamDayStatus =
  | "CHECKED_IN"
  | "CHECKED_OUT"
  | "PARTIAL"
  | "ON_LEAVE"
  | "NOT_WORKING"
  | "NO_CHECK_IN"
  | "NOT_STARTED";

export interface TeamMember {
  employeeId: string;
  name: string;
  empCode: string;
  departmentPath: string | null;
  role: string | null;
}

export interface TeamDayRow extends TeamMember {
  date: string;
  dayStatus: TeamDayStatus;
  checkInAt: string | null;
  checkOutAt: string | null;
  workedMinutes: number | null;
  workMode: WorkMode | null;
}

export interface TeamSummary {
  date: string;
  teamSize: number;
  checkedIn: number;
  checkedOut: number;
  onLeave: number;
  notWorking: number;
  noCheckIn: number;
}

export interface TeamRequestRow {
  id: string;
  employeeId: string;
  name: string;
  empCode: string;
  type: RequestType;
  /** The tenant's name for the type, or null (FEAT-2026-031). */
  typeLabel?: string | null;
  date: string;
  dateTo: string | null;
  status: RequestStatus;
  reason: string;
  createdAt: string;
  decidedAt: string | null;
}

/**
 * One direct report's request for their line manager (FEAT-2026-027, BR-211).
 * `metadata` is allowlisted by the server: named sites and times, never place.
 */
export interface TeamRequestDetail {
  id: string;
  employeeId: string;
  name: string;
  empCode: string;
  type: RequestType;
  /** The tenant's name for the type, or null (FEAT-2026-031). */
  typeLabel?: string | null;
  date: string;
  dateTo: string | null;
  status: RequestStatus;
  reason: string;
  requestedChange: string;
  currentState: string | null;
  metadata: {
    v?: number;
    overtime?: { startTime?: string; endTime?: string; durationMinutes?: number; endsNextDay?: boolean };
    correctedSite?: { siteName?: string };
    recordedState?: {
      date?: string;
      firstCheckInAt?: string | null;
      lastCheckOutAt?: string | null;
      siteName?: string | null;
      totalWorkedMinutes?: number | null;
    };
  } | null;
  attachments: Array<{ url: string; filename: string; mimeType: string; bytes: number }>;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  decidedByName: string | null;
  decidedByRole: "LINE_MANAGER" | "HR" | null;
  /** Pending or needs clarification: the manager may still decide. */
  canDecide: boolean;
}

export type TeamDecision = "approve" | "reject" | "needs-clarification";

export type RequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "NEEDS_CLARIFICATION"
  | "CANCELLED";

export type RequestType =
  | "MISSED_CHECK_IN"
  | "MISSED_CHECK_OUT"
  | "INCORRECT_LOCATION"
  | "ATTENDANCE_CORRECTION"
  | "LEAVE"
  | "OVERTIME"
  | "SHIFT_SWAP"
  | "WORK_FROM_ANYWHERE"
  | "OTHER";

export interface EmployeeRequest {
  id: string;
  date: string;
  /** Last day of a range: Leave, Work from anywhere, Other in range mode, and past Shift swaps. */
  dateTo: string | null;
  type: RequestType;
  /** The tenant's name for the type, or null (FEAT-2026-031). */
  typeLabel?: string | null;
  status: RequestStatus;
  requestedChange: string;
  reason: string;
  currentState: string | null;
  approverId: string | null;
  approvedAt: string | null;
  approvalNotes: string | null;
  /** Who made the current decision (FEAT-2026-027). Null unless decided. */
  decidedByName: string | null;
  decidedByRole: "LINE_MANAGER" | "HR" | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRequestBody {
  date: string;
  dateTo?: string;
  type: RequestType;
  requestedChange: string;
  reason: string;
  currentState?: string;
  /** Already-uploaded files, by their server-issued `key` (BR-101). */
  attachments?: AttachmentRef[];
}

/** A file uploaded through an `UploadTicket`, sent with a new request. */
export interface AttachmentRef {
  key: string;
  filename: string;
  mimeType: string;
  bytes: number;
}

/** `POST /mobile/requests/attachments/upload-url`: where and how to PUT the bytes. */
export interface UploadTicket {
  key: string;
  uploadUrl: string;
  headers: Record<string, string>;
}

export interface HomeLocation {
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  radiusM: number | null;
  /** The radius WFH checks actually use — the employee's, else the tenant default. */
  effectiveRadiusM: number;
  updatedAt: string | null;
}

export interface SetHomeLocationBody {
  latitude: number;
  longitude: number;
  address?: string;
  radiusM?: number;
}

export type CheckInRefusalCode =
  | "ALREADY_CHECKED_IN"
  | "ALREADY_COMPLETED_TODAY"
  | "ALREADY_AT_THIS_SITE"
  | "OUTSIDE_GEOFENCE"
  | "IMPRECISE_LOCATION";

/**
 * The server's own verdict on a punch, before it is made (BR-153).
 *
 * Advisory. Never disable the check-in control on `allowed: false` — the
 * position may have moved by the time the button is pressed, and a disabled
 * control means the server never gets to decide. Warn, and let the real call
 * refuse.
 */
export interface CheckInPreview {
  allowed: boolean;
  workMode: WorkMode;
  geofenceStatus: GeofenceStatus;
  gpsStatus: GpsStatus;
  accuracyM: number | null;
  distanceM: number | null;
  wfaRequestId: string | null;
  site: { id: string; name: string } | null;
  refusal: { code: CheckInRefusalCode; message: string } | null;
}

function query(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const employeeApi = {
  /**
   * Identity. The employee-lane counterpart of `/auth/me`.
   *
   * `suppressAuthFailure` because this doubles as a probe at boot: a 401 here
   * means "this Clerk user is not an employee", which is an ordinary answer
   * for an admin — not a reason to tear down their session.
   */
  me: () => apiFetch<EmployeeSelf>("/mobile/profile", { suppressAuthFailure: true }),

  config: () => apiFetch<EmployeeConfig>("/mobile/config"),

  today: () => apiFetch<EmployeeDay>("/mobile/attendance/today"),

  current: () =>
    apiFetch<{ session: EmployeeSession | null }>("/mobile/attendance/current"),

  /** Where the employee may clock in, for drawing the geofence (FEAT-2026-025). */
  sites: () => apiFetch<{ data: EmployeeSite[] }>("/mobile/sites"),

  /** Paginated, newest first; both bounds inclusive (BR-026). */
  history: (params: {
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) q.set(k, String(v));
    }
    return apiFetch<Paginated<EmployeeDay>>(
      `/mobile/attendance/history?${q.toString()}`,
    );
  },

  /**
   * The web-specific routes. They exist so the server learns the origin from
   * the URL rather than from a field the caller controls — see the controller
   * comment on `webCheckIn`. Do not repoint these at the plain
   * `/mobile/attendance/check-in`: that would silently record browser punches
   * as mobile ones and skip the tenant's web-check-in switch.
   */
  checkIn: (body: CheckInBody) =>
    apiFetch<EmployeeDay>("/mobile/attendance/web/check-in", {
      method: "POST",
      body,
    }),

  checkOut: (body: CheckOutBody) =>
    apiFetch<EmployeeDay>("/mobile/attendance/web/check-out", {
      method: "POST",
      body,
    }),

  /**
   * Same body as a real punch, no write. Used to warn *before* the employee
   * spends a check-in (BR-153).
   *
   * Note the `web/` segment: the preview must be judged on the same lane the
   * punch will take, or a tenant with web check-in disabled would be told the
   * punch is fine and then get a 403 from `checkIn`.
   */
  previewCheckIn: (body: CheckInBody) =>
    apiFetch<CheckInPreview>("/mobile/attendance/web/check-in/preview", {
      method: "POST",
      body,
    }),

  /**
   * The employee's own schedule. Resolved by the same code as the
   * missed-attendance sweep, so this can never disagree with what the system
   * would chase them for. Defaults to the next 14 days; capped at 92.
   */
  schedule: (params: { dateFrom?: string; dateTo?: string } = {}) =>
    apiFetch<EmployeeSchedule>(`/mobile/attendance/schedule${query(params)}`),

  // --- Notifications -------------------------------------------------------
  // The employee's own only; admin-audience notifications are never returned
  // on this lane.

  notifications: (params: {
    page?: number;
    pageSize?: number;
    unreadOnly?: boolean;
    type?: NotificationType;
  } = {}) =>
    apiFetch<Paginated<NotificationItem>>(`/mobile/notifications${query(params)}`),

  unreadCount: () =>
    apiFetch<{ count: number }>("/mobile/notifications/unread-count"),

  markNotificationRead: (id: string) =>
    apiFetch<NotificationItem>(`/mobile/notifications/${id}/read`, {
      method: "PATCH",
    }),

  markAllNotificationsRead: () =>
    apiFetch<{ updated: number }>("/mobile/notifications/mark-all-read", {
      method: "POST",
    }),

  // --- Requests ------------------------------------------------------------

  requests: (params: {
    page?: number;
    pageSize?: number;
    status?: RequestStatus;
    type?: RequestType;
  } = {}) => apiFetch<Paginated<EmployeeRequest>>(`/mobile/requests${query(params)}`),

  uploadUrl: (body: { filename: string; mimeType: string; bytes: number }) =>
    apiFetch<UploadTicket>("/mobile/requests/attachments/upload-url", { method: "POST", body }),
  createRequest: (body: CreateRequestBody) =>
    apiFetch<EmployeeRequest>("/mobile/requests", { method: "POST", body }),

  /** Withdraw. Only PENDING / NEEDS_CLARIFICATION may be cancelled (BR-032). */
  cancelRequest: (id: string) =>
    apiFetch<EmployeeRequest>(`/mobile/requests/${id}/cancel`, { method: "POST" }),

  // --- Profile -------------------------------------------------------------

  /**
   * Home location is **employee self-service only** (BR-063) — HR cannot set
   * it on someone's behalf, so without this screen a web-only employee can
   * never be classified WFH.
   */
  homeLocation: () => apiFetch<HomeLocation>("/mobile/profile/home-location"),

  setHomeLocation: (body: SetHomeLocationBody) =>
    apiFetch<HomeLocation>("/mobile/profile/home-location", {
      method: "PUT",
      body,
    }),

  // --- My team (FEAT-2026-026): direct reports only, read only -------------

  team: {
    summary: (date?: string) => apiFetch<TeamSummary>(`/mobile/team/summary${query({ date })}`),
    day: (params: { date?: string; search?: string; page?: number; pageSize?: number }) =>
      apiFetch<Paginated<TeamDayRow>>(`/mobile/team/day${query(params)}`),
    memberDays: (id: string, params: { from?: string; to?: string }) =>
      apiFetch<{ member: TeamMember; days: TeamDayRow[] }>(
        `/mobile/team/members/${encodeURIComponent(id)}/days${query(params)}`,
      ),
    requests: (params: { status?: RequestStatus; waiting?: boolean; page?: number; pageSize?: number }) =>
      apiFetch<Paginated<TeamRequestRow>>(`/mobile/team/requests${query(params)}`),
    /** A direct report's request in full (FEAT-2026-027); 404 unless it's yours to decide. */
    request: (id: string) =>
      apiFetch<TeamRequestDetail>(`/mobile/team/requests/${encodeURIComponent(id)}`),
    decide: (id: string, decision: TeamDecision, notes?: string) =>
      apiFetch<TeamRequestDetail>(`/mobile/team/requests/${encodeURIComponent(id)}/${decision}`, {
        method: "PATCH",
        body: { notes },
      }),
  },
};
